import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_ENDPOINTS } from '../../config/apiConfig';
import {
  ACCENT_THEMES,
  CHROME_THEMES,
  DEFAULT_ACCENT_THEME,
  DEFAULT_CHROME_THEME,
  applyOrgBranding,
  getAccentThemeLabel,
  getChromeThemeLabel,
  normalizeOrgBranding,
  setStoredOrgBranding,
  updateSessionUserOrgBranding,
  ORG_BRANDING_EVENT,
} from '../../shared/utils/orgBranding';
import '../../shared/components/common/ThemePickers.css';

function BrandingSwatches({ themes, value, onSelect, ariaLabel, classPrefix }) {
  return (
    <div className="shell-theme-picker-swatches" role="listbox" aria-label={ariaLabel}>
      {themes.map((theme) => (
        <button
          key={theme.id}
          type="button"
          role="option"
          aria-selected={value === theme.id}
          className={`shell-theme-dot ${classPrefix}--${theme.id}${value === theme.id ? ' active' : ''}`}
          onClick={() => onSelect(theme.id)}
          title={`${theme.label} — ${theme.description}`}
          aria-label={theme.label}
        />
      ))}
    </div>
  );
}

export default function OrgBrandingCard({ token, refreshTrigger = 0 }) {
  const [branding, setBranding] = useState({
    chrome_theme: DEFAULT_CHROME_THEME,
    accent_theme: DEFAULT_ACCENT_THEME,
  });
  const [defaults, setDefaults] = useState({
    chrome_theme: DEFAULT_CHROME_THEME,
    accent_theme: DEFAULT_ACCENT_THEME,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBranding = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(API_ENDPOINTS.SYSTEM_CONTROLS.BRANDING, { headers });
      const next = normalizeOrgBranding(response.data?.branding || {});
      setBranding(next);
      setDefaults(normalizeOrgBranding(response.data?.defaults || {}));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load organization branding.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding, refreshTrigger]);

  const preview = (patch) => {
    const next = normalizeOrgBranding({ ...branding, ...patch });
    setBranding(next);
    applyOrgBranding(next);
  };

  const save = async () => {
    if (!token) return;
    setSaving(true);
    setError('');
    try {
      const response = await axios.put(
        API_ENDPOINTS.SYSTEM_CONTROLS.BRANDING,
        { branding },
        { headers }
      );
      const saved = normalizeOrgBranding(response.data?.branding || branding);
      setBranding(saved);
      applyOrgBranding(saved);
      setStoredOrgBranding(saved);
      updateSessionUserOrgBranding(saved);
      window.dispatchEvent(new Event(ORG_BRANDING_EVENT));
      toast.success('Organization branding saved for all users.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save organization branding.');
      toast.error('Could not save organization branding.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="system-controls-state">Loading organization branding…</div>;
  }

  const isDefault =
    branding.chrome_theme === defaults.chrome_theme
    && branding.accent_theme === defaults.accent_theme;

  return (
    <div className="system-controls-subsection-panel org-branding-card">
      {error ? <div className="system-controls-error">{error}</div> : null}

      <div className="system-controls-subsection-toolbar">
        <div className="org-branding-card-note">
          Saved branding applies to this organization everywhere — including login and public pages.
        </div>
        <div className="system-controls-subsection-toolbar-actions action-palette">
          <button
            type="button"
            className="btn-refresh"
            onClick={fetchBranding}
            disabled={saving}
            title="Refresh branding"
            aria-label="Refresh branding"
          >
            🔄
          </button>
          <button type="button" className="action-btn action-primary" onClick={save} disabled={saving}>
            <span className="action-text">{saving ? 'Saving…' : 'Save branding'}</span>
          </button>
        </div>
      </div>

      <hr className="system-controls-subsection-divider" />

      <div className="org-branding-pickers">
        <div className="shell-theme-picker shell-theme-picker--page">
          <div className="shell-theme-picker-label">Chrome (nav &amp; header)</div>
          <BrandingSwatches
            themes={CHROME_THEMES}
            value={branding.chrome_theme}
            onSelect={(id) => preview({ chrome_theme: id })}
            ariaLabel="Chrome theme"
            classPrefix="brand-chrome-dot"
          />
          <div className="shell-theme-picker-current">{getChromeThemeLabel(branding.chrome_theme)}</div>
        </div>

        <div className="shell-theme-picker shell-theme-picker--page">
          <div className="shell-theme-picker-label">Accent (buttons &amp; active nav)</div>
          <BrandingSwatches
            themes={ACCENT_THEMES}
            value={branding.accent_theme}
            onSelect={(id) => preview({ accent_theme: id })}
            ariaLabel="Accent theme"
            classPrefix="brand-accent-dot"
          />
          <div className="shell-theme-picker-current">{getAccentThemeLabel(branding.accent_theme)}</div>
        </div>
      </div>

      <p className="org-branding-card-footnote">
        {isDefault
          ? 'Using platform defaults (clinical teal chrome, teal accent).'
          : 'Custom organization branding is active.'}
      </p>
    </div>
  );
}
